import { _ as _assign, d as __spreadArray } from './tslib.es6-604585c1.js';
import { jsx } from 'react/jsx-runtime';
import { createContext, useContext, useMemo, useState, useEffect, useCallback } from 'react';
import { A as AuthFlow, f as AuthActionOptions, g as StatusOptions } from './constants-3bc57a75.js';
import { checkIfRecentLoginMethodExists } from './services/recentLoginMethods/index.js';
import { usePulseSettings } from '@pulse/react';
import { d as defaultState, A as AuthModalDataForms, I as InitialPage } from './constants-1c6c7b5f.js';

var defaultRemoteSettings = {
  optionsForm: {
    title: 'Before you jump in...',
    description: 'Create an account to save your work. Or sign in with email or username if you already have a Picsart account.',
    authOptions: {
      email: 'Continue with email | username'
    },
    buttonsOptions: [{
      name: 'email',
      primary: true,
      show: true
    }, {
      name: 'divider',
      show: true
    }, {
      name: 'google',
      show: true,
      showIcon: true
    }, {
      name: 'facebook',
      show: true,
      showIcon: true
    }, {
      name: 'apple',
      show: true,
      showIcon: true
    }]
  },
  recentLoginMethodsForm: {
    show: true,
    title: 'Jump back in',
    authButtonPrefix: 'Continue with',
    buttonTitle: 'Continue with another account'
  },
  modalVisuals: {
    modalImage: {
      url: 'https://pastatic.picsart.com/cms-pastatic/56e5f8b2-343c-4b3e-93cd-96be7a21ca9c.svg',
      alt: 'girl and effects'
    },
    reversed: false
  },
  emailForm: {
    title: 'Welcome!',
    description: 'Create your free account with email. Already have a Picsart account? Log in with your email or username.',
    placeholder: 'Email | username',
    label: 'Your email | username',
    wrongEmail: 'That email doesn’t seem quite right. Check the format and try again.',
    wrongUsername: 'Looks like that username doesn’t exist.',
    handleSSOExistence: false
  },
  createPasswordForm: {
    title: 'Welcome to Picsart',
    description: 'Create your account using',
    placeholder: 'Unique password',
    label: 'Create a password',
    showUsernameFormOnSignIn: false,
    showUsernameFormOnSignUp: false,
    biometricCheckBoxTxt: 'I agree to Picsart`s <a target=“_blank” href={{termsLink}} class={{linkClass}}>Terms of Use</a> ' + 'and <a target=“_blank” href={{privacyPolicyLink}} class={{linkClass}}>Privacy Policy</a>, including consent to the collection, use, and storage of my biometric data.',
    submitBtnTxt: 'Continue'
  },
  insertPasswordForm: {
    title: 'Welcome back',
    description: 'Log in to your Picsart account',
    wrongPassword: 'Looks like that\'s the wrong password',
    label: 'Enter your password',
    placeholder: 'Password',
    trouble: 'Trouble logging in?'
  },
  noInternetForm: {
    title: 'No internet connection',
    description: 'Maybe the stars aren’t aligned or maybe it’s a poor signal. Either way, your account registration or log in isn’t finished yet. Check your connection and try again.'
  },
  welcomeBackEmailCodeAndSSO: {
    title: 'Welcome back!',
    description: "You previously logged in using {{sso_names}}. To login with email, check for the code sent to {{email}}. Didn't get it? ",
    resendButtonTxt: 'Resend',
    codeButtonTxt: 'Login with code',
    resendInterval: 30,
    resendButtonTimerTxt: 'Resend in {{time}} seconds'
  },
  welcomeBackMultipleSSO: {
    title: 'Welcome back!',
    onlySSODescription: 'It looks like you`ve logged in with multiple accounts before. Choose your preferred method:',
    emailAndSSODescription: '{{email}} is connected to several accounts.'
  },
  usernameForm: {
    title: 'Final step: pick a username',
    description: 'This is how you’ll appear to other Picsart creators.',
    inputLabel: 'Username',
    biometricCheckBoxTxt: 'I agree to Picsart`s <a target=“_blank” href={{termsLink}} class={{linkClass}}>Terms of Use</a> ' + 'and <a target=“_blank” href={{privacyPolicyLink}} class={{linkClass}}>Privacy Policy</a>, including consent to the collection, use, and storage of my biometric data.',
    buttonTxt: 'Finish setting up'
  },
  usernameFormSSO: {
    title: 'Final step: pick a username',
    description: 'This is how you’ll appear to other Picsart creators.',
    inputLabel: 'Username',
    biometricCheckBoxTxt: 'I agree to Picsart`s <a target=“_blank” href={{termsLink}} class={{linkClass}}>Terms of Use</a> ' + 'and <a target=“_blank” href={{privacyPolicyLink}} class={{linkClass}}>Privacy Policy</a>, including consent to the collection, use, and storage of my biometric data.',
    buttonTxt: 'Create account'
  }
};

var RemoteSettingsContext = /*#__PURE__*/createContext({
  settings: defaultRemoteSettings,
  loading: false
});
var useRemoteSettingsContext = function useRemoteSettingsContext() {
  return useContext(RemoteSettingsContext);
};
var RemoteSettingsProvider = function RemoteSettingsProvider(_a) {
  var children = _a.children;
  var _b = usePulseSettings({
      name: 'registration_modal_json',
      tag: 'registration_modal_json'
    }),
    settings = _b.settings,
    loading = _b.loading;
  return jsx(RemoteSettingsContext.Provider, _assign({
    value: {
      settings: _assign(_assign({}, defaultRemoteSettings), settings),
      loading: loading
    }
  }, {
    children: children
  }));
};

var useAuthModalData = function useAuthModalData(form) {
  var _a = useRemoteSettingsContext(),
    settings = _a.settings,
    loading = _a.loading;
  var data = useMemo(function () {
    return _assign(_assign({}, settings[form]), {
      loading: loading
    });
  }, [form, loading, settings]);
  return data;
};

var RegistrationFormContext = /*#__PURE__*/createContext(defaultState);
var useRegistrationFormContext = function useRegistrationFormContext() {
  return useContext(RegistrationFormContext);
};
function RegistrationFormProvider(_a) {
  var children = _a.children,
    onPageChange = _a.onPageChange;
  var show = useAuthModalData(AuthModalDataForms.recentLoginMethodsForm).show;
  var isRecentLoginMethodsAvialable = useMemo(function () {
    return show && checkIfRecentLoginMethodExists();
  }, [show]);
  var flow = isRecentLoginMethodsAvialable ? AuthFlow.RecentLoginMethods : AuthFlow.Default;
  var _b = useState(InitialPage[flow]),
    currentPage = _b[0],
    setCurrentPage = _b[1];
  var _c = useState([]),
    pagesHistory = _c[0],
    setPagesHistory = _c[1];
  var _d = useState(''),
    email = _d[0],
    setEmail = _d[1];
  var _e = useState(''),
    username = _e[0],
    setUsername = _e[1];
  var _f = useState(''),
    password = _f[0],
    setPassword = _f[1];
  var _g = useState(''),
    geoZone = _g[0],
    setGeoZone = _g[1];
  var _h = useState(AuthActionOptions.SignIn),
    action = _h[0],
    setAuthAction = _h[1];
  var _j = useState({}),
    checkedStates = _j[0],
    setCheckedStates = _j[1];
  var _k = useState({
      provider: '',
      credential: ''
    }),
    socialData = _k[0],
    setSocialData = _k[1];
  var _l = useState(null),
    providers = _l[0],
    setProviders = _l[1];
  var _m = useState({
      type: StatusOptions.Default,
      message: ''
    }),
    status = _m[0],
    setStatus = _m[1];
  var _o = useState(null),
    stagedUserInfo = _o[0],
    setStagedUserInfo = _o[1];
  var _p = useState(false),
    isIncognito = _p[0],
    setIsIncognito = _p[1];
  useEffect(function () {
    onPageChange === null || onPageChange === void 0 ? void 0 : onPageChange(currentPage);
  }, [currentPage]);
  var changeCurrentPage = useCallback(function (page) {
    setStatus({
      type: StatusOptions.Default,
      message: ''
    });
    if (currentPage) {
      setPagesHistory(function (prevHistory) {
        return __spreadArray(__spreadArray([], prevHistory, true), [currentPage], false);
      });
    }
    setCurrentPage(page);
  }, [currentPage]);
  var changeCheckedStates = useCallback(function (name) {
    setCheckedStates(function (prev) {
      var _a;
      return _assign(_assign({}, prev), (_a = {}, _a[name] = !prev[name], _a));
    });
  }, []);
  var goPrevPage = useCallback(function () {
    setStatus({
      type: StatusOptions.Default,
      message: ''
    });
    setPagesHistory(function (prevHistory) {
      if (prevHistory.length === 0) return prevHistory;
      var lastPage = prevHistory[prevHistory.length - 1];
      setCurrentPage(lastPage);
      return prevHistory.slice(0, -1);
    });
  }, []);
  var memoizedValues = useMemo(function () {
    return _assign(_assign({}, defaultState), {
      flow: flow,
      email: email,
      username: username,
      password: password,
      currentPage: currentPage,
      pagesHistory: pagesHistory,
      geoZone: geoZone,
      action: action,
      status: status,
      checkedStates: checkedStates,
      socialData: socialData,
      providers: providers,
      stagedUserInfo: stagedUserInfo,
      isIncognito: isIncognito,
      setEmail: setEmail,
      setUsername: setUsername,
      setAuthAction: setAuthAction,
      changeCheckedStates: changeCheckedStates,
      setGeoZone: setGeoZone,
      setPassword: setPassword,
      setStagedUserInfo: setStagedUserInfo,
      setCurrentPage: changeCurrentPage,
      goPrevPage: goPrevPage,
      setStatus: setStatus,
      setCheckedStates: setCheckedStates,
      setSocialData: setSocialData,
      setProviders: setProviders,
      setIsIncognito: setIsIncognito
    });
  }, [flow, email, username, password, currentPage, pagesHistory, geoZone, action, status, checkedStates, socialData, providers, stagedUserInfo, isIncognito, changeCheckedStates, changeCurrentPage, goPrevPage]);
  return jsx(RegistrationFormContext.Provider, _assign({
    value: memoizedValues
  }, {
    children: children
  }));
}

export { RegistrationFormProvider as R, useAuthModalData as a, useRemoteSettingsContext as b, RemoteSettingsProvider as c, useRegistrationFormContext as u };
//# sourceMappingURL=RegistrationFormContext-de1e9c93.js.map
